// Puerto de packages/hce/src/hce/copiaHistoria/pdfCopiaHistoria.ts —
// rediseñado 2026-09-27 (pedido explícito de Sue): antes armaba un RESUMEN en
// texto (una línea por evento, sin los campos reales de cada formulario). "Un
// solo documento que contenga toda la información de todos los campos
// completados" ya existe — es cada PDF firmado (PanelFirmaImpresion.tsx ya
// renderiza el formulario completo, campo por campo, al firmar). En vez de
// reinventar ese renderizado acá, esta función arma una portada + índice y
// FUSIONA (copia todas las páginas de) cada PDF firmado real del paciente, en
// orden cronológico — única fuente de verdad, sin duplicar lógica de armado.
import type { MedplumClient } from '@medplum/core';
import type { Patient, Reference } from '@medplum/fhirtypes';
import { crearCursorPdf, NAVY } from '@logiacare/hce-pdf';
import { PDFDocument } from 'pdf-lib';
import { cargarFuentesPdfNavegador } from '../lib/fuentesPdfNavegador';
import { esDocumentoFirmado, fechaFirmaDe, tituloDocumentoFirmado } from '../lib/documentosFirmados';
import { mapConcurrente } from '../lib/concurrencia';

/** Descargas en paralelo a la vez (Binary de Medplum) — ni 1 (lento, secuencial) ni "todas de una" (satura la conexión/el hilo de fetch del navegador). */
const CONCURRENCIA_DESCARGA = 6;

function fechaLegible(iso: string | undefined): string {
  if (!iso) return 's/f';
  try {
    return new Date(iso).toLocaleString('es-EC');
  } catch {
    return iso;
  }
}

export async function generarPdfCopiaHistoria(
  medplum: MedplumClient,
  patient: Patient,
  subject: Reference<Patient>,
  onProgreso?: (completados: number, total: number) => void,
): Promise<Uint8Array> {
  const patientRef = subject.reference ?? `Patient/${patient.id}`;
  const documentos = (await medplum.searchResources('DocumentReference', { patient: patientRef, _count: 200 }))
    .filter(esDocumentoFirmado)
    .sort((a, b) => new Date(fechaFirmaDe(a) ?? 0).getTime() - new Date(fechaFirmaDe(b) ?? 0).getTime());

  const cursor = await crearCursorPdf(await cargarFuentesPdfNavegador());
  const pacienteNombre = patient.name?.[0] ? [patient.name[0].given?.join(' '), patient.name[0].family].filter(Boolean).join(' ') : patientRef;
  cursor.texto('Copia de la historia clínica', { serif: true, size: 22, color: NAVY });
  cursor.espacio(4);
  cursor.linea();
  cursor.etiqueta('Paciente');
  cursor.texto(`${pacienteNombre}${patient.identifier?.[0]?.value ? ` (${patient.identifier[0].value})` : ''}`, { bold: true, size: 12 });
  cursor.espacio(2);
  cursor.etiqueta('Generado');
  cursor.texto(new Date().toLocaleString('es-EC'), { size: 9.5 });
  cursor.espacio(10);
  cursor.linea();

  cursor.etiqueta(`Índice — ${documentos.length} documento(s) firmado(s)`, { color: NAVY });
  cursor.espacio(4);
  if (documentos.length === 0) {
    cursor.texto('Este paciente todavía no tiene ningún formulario guardado y firmado.', { size: 9.5 });
  } else {
    for (const d of documentos) {
      cursor.texto(`${fechaLegible(fechaFirmaDe(d))} — ${tituloDocumentoFirmado(d)}`, { size: 9.5 });
    }
  }

  // Descargar + parsear los N PDFs firmados es lo lento (una petición de red
  // por documento) — antes era secuencial (uno por uno, ~1-2s cada uno con
  // historiales grandes tardaba minutos). Se paraleliza la parte de I/O
  // (`CONCURRENCIA_DESCARGA` a la vez) y se reporta progreso a medida que
  // cada descarga termina; el ORDEN cronológico del documento final no se
  // toca — `cargados` guarda cada `PDFDocument` en su índice fijo (`indice`
  // de `mapConcurrente`), y el copiado a `cursor.doc` de abajo sigue
  // recorriendo `documentos` en el mismo orden de siempre.
  let completados = 0;
  const cargados = await mapConcurrente(documentos, CONCURRENCIA_DESCARGA, async (d) => {
    const url = d.content?.[0]?.attachment?.url;
    if (!url) return null;
    try {
      const blob = await medplum.download(url);
      const bytes = new Uint8Array(await blob.arrayBuffer());
      return await PDFDocument.load(bytes);
    } catch (err) {
      // Un PDF individual roto/inaccesible no debe tumbar la copia completa —
      // mismo criterio "sin dato para esa sección" que buscarOVacio en
      // historiaClinica.ts.
      console.error(`[pdfCopiaHistoria] no se pudo incluir "${tituloDocumentoFirmado(d)}" (${d.id}): ${err instanceof Error ? err.message : String(err)}`);
      return null;
    } finally {
      completados++;
      onProgreso?.(completados, documentos.length);
    }
  });

  // La portada/índice queda en las primeras páginas de `cursor.doc`; a partir
  // de acá se APPENDEAN las páginas reales de cada PDF firmado, copiadas tal
  // cual (mismas primitivas de pdf-lib que ya usa crearCursorPdf por dentro
  // — `cursor.doc` es el mismo `PDFDocument`, ver layout.ts). Este copiado en
  // sí es CPU (sin red), rápido incluso con decenas de documentos — no hace
  // falta paralelizarlo, y `copyPages`/`addPage` no son thread-safe entre sí
  // de todos modos (mismo `cursor.doc` de destino).
  for (const origen of cargados) {
    if (!origen) continue;
    const paginas = await cursor.doc.copyPages(origen, origen.getPageIndices());
    for (const pagina of paginas) cursor.doc.addPage(pagina);
  }

  return cursor.guardar();
}
